// src/services/approvalDecisionRequest.ts - the exact call an approvals-queue
// decision sends, and the staff request hash of it (ENG-1527).
//
// A staff confirmation binds the approve or reject call itself (ENG-1520): its
// path carries the approval id and the decision, its body the comment or
// reason. The approver is shown the hash of that call, so the queue must send
// exactly the call built here: useApprovalActions sends this, and the hash is
// computed the way the confirmation engine's binder computes it
// (staffConfirmation/bindStaffConfirmation.ts): the URL the client resolves,
// its canonical path, and the RFC 8785 body.
import { axiosInstance } from "./apiService";
import {
  canonicalPath,
  canonicalRequestBody,
  staffRequestHash,
} from "./staffConfirmation/requestHash";

export const APPROVALS_ENDPOINT = "/api/v2/admin/approvals";

export type ApprovalDecision = "approve" | "reject" | "cancel";

export interface ApprovalDecisionRequest {
  method: "POST";
  path: string;
  body: Record<string, string> | undefined;
}

/**
 * Approve carries an optional comment, reject a reason (the api refuses one
 * without), cancel an optional reason. Text is trimmed; an empty optional text
 * sends no body.
 */
export const approvalDecisionRequest = (
  id: string,
  decision: ApprovalDecision,
  text?: string
): ApprovalDecisionRequest => {
  const trimmed = (text ?? "").trim();
  const body =
    decision === "reject"
      ? { reason: trimmed }
      : trimmed
        ? decision === "approve"
          ? { comment: trimmed }
          : { reason: trimmed }
        : undefined;
  return {
    method: "POST",
    path: `${APPROVALS_ENDPOINT}/${id}/${decision}`,
    body,
  };
};

type UriOf = (config: { url?: string; method?: string }) => string;

const clientUri: UriOf = (config) => axiosInstance.getUri(config);

/** The staff request hash of one decision call, as the binder will compute it. */
export const approvalDecisionHash = (
  request: ApprovalDecisionRequest,
  uriOf: UriOf = clientUri
): Promise<string> => {
  const url = new URL(
    uriOf({ url: request.path, method: request.method.toLowerCase() }),
    window.location.origin
  );
  return staffRequestHash({
    method: request.method,
    path: canonicalPath(url.pathname, url.search),
    body: canonicalRequestBody(request.body),
  });
};
